import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOperatorDashboard,
  fetchOperatorHouseDetail,
  fetchOperatorHousesSummary,
} from "../redux/operatorConsoleSlice";
import { shouldSyncDashboard } from "../utils/operatorConsoleHelpers";

const detailStatusFor = (map, houseId) => map[String(houseId)] || "idle";

const houseNeedsDetail = (house) => Boolean(house?._operatorSummary);

/**
 * Staged operator market loading:
 * 1. Summary list (fast) → show default house shell
 * 2. Full detail for active house
 * 3. Dashboard for active house
 * 4. Background prefetch for other houses (+ their dashboards when live)
 */
export function useOperatorMarketBootstrap(userId, houseId) {
  const dispatch = useDispatch();
  const {
    houses,
    housesStatus,
    houseDetailStatusById,
    dashboardStatusByHouse,
    dashboardsByHouse,
  } = useSelector((s) => s.operatorConsole);

  const prefetchedHousesRef = useRef(new Set());
  const prefetchedDashboardsRef = useRef(new Set());

  const loadHouseDetail = useCallback(
    (betId) => {
      if (!userId || !betId) return Promise.resolve();
      const key = String(betId);
      const status = detailStatusFor(houseDetailStatusById, key);
      if (status === "loading" || status === "succeeded") {
        return Promise.resolve();
      }
      return dispatch(fetchOperatorHouseDetail({ userId, betId: key }));
    },
    [dispatch, userId, houseDetailStatusById]
  );

  const loadDashboard = useCallback(
    (targetHouseId) => {
      if (!targetHouseId) return Promise.resolve();
      const key = String(targetHouseId);
      const house = houses.find((h) => String(h._id) === key);
      const statusKey = String(house?.status || "draft").toLowerCase();
      if (!shouldSyncDashboard(statusKey)) return Promise.resolve();

      const dashStatus = dashboardStatusByHouse[key] || "idle";
      if (dashStatus === "loading" || dashboardsByHouse[key]) {
        return Promise.resolve();
      }
      return dispatch(fetchOperatorDashboard(key));
    },
    [dispatch, houses, dashboardStatusByHouse, dashboardsByHouse]
  );

  useEffect(() => {
    if (!userId) return;
    if (housesStatus === "idle") {
      dispatch(fetchOperatorHousesSummary(userId));
    }
  }, [dispatch, userId, housesStatus]);

  useEffect(() => {
    if (!userId || !houseId || housesStatus !== "succeeded") return;
    const house = houses.find((h) => String(h._id) === String(houseId));
    if (!house || !houseNeedsDetail(house)) return;
    loadHouseDetail(houseId);
  }, [userId, houseId, houses, housesStatus, loadHouseDetail]);

  useEffect(() => {
    if (!houseId || housesStatus !== "succeeded") return;
    loadDashboard(houseId);
  }, [houseId, housesStatus, loadDashboard]);

  useEffect(() => {
    if (!userId || !houseId || housesStatus !== "succeeded") return;
    const activeReady =
      detailStatusFor(houseDetailStatusById, houseId) === "succeeded" ||
      !houseNeedsDetail(houses.find((h) => String(h._id) === String(houseId)));
    const activeDashboardReady =
      Boolean(dashboardsByHouse[String(houseId)]) ||
      dashboardStatusByHouse[String(houseId)] === "failed";

    if (!activeReady && !activeDashboardReady) return;

    for (const house of houses) {
      const id = String(house._id);
      if (id === String(houseId)) continue;

      if (houseNeedsDetail(house) && !prefetchedHousesRef.current.has(id)) {
        const status = detailStatusFor(houseDetailStatusById, id);
        if (status === "idle" || status === "failed") {
          prefetchedHousesRef.current.add(id);
          dispatch(fetchOperatorHouseDetail({ userId, betId: id }));
        }
      }

      const statusKey = String(house.status || "draft").toLowerCase();
      if (
        shouldSyncDashboard(statusKey) &&
        !prefetchedDashboardsRef.current.has(id) &&
        !dashboardsByHouse[id]
      ) {
        const dashStatus = dashboardStatusByHouse[id] || "idle";
        if (dashStatus === "idle" || dashStatus === "failed") {
          prefetchedDashboardsRef.current.add(id);
          dispatch(fetchOperatorDashboard(id));
        }
      }
    }
  }, [
    userId,
    houseId,
    houses,
    housesStatus,
    houseDetailStatusById,
    dashboardStatusByHouse,
    dashboardsByHouse,
    dispatch,
  ]);

  useEffect(() => {
    prefetchedHousesRef.current = new Set();
    prefetchedDashboardsRef.current = new Set();
  }, [userId]);
}

export default useOperatorMarketBootstrap;
